/* eslint-disable @typescript-eslint/no-explicit-any */
import { UA, debug, WebSocketInterface } from "@krivega/jssip";
import type { SoftPhoneConfig, CallState } from "./types";

type EventType =
  | "connecting"
  | "connected"
  | "registered"
  | "disconnected"
  | "registrationFailed";

type CallActionType =
  | "reinvite"
  | "incomingCall"
  | "outgoingCall"
  | "callProgress"
  | "callEnded"
  | "callAccepted"
  | "callRejected"
  | "hold"
  | "unhold"
  | "unmute"
  | "mute"
  | "notify";

export class CallsFlowControl {
  public ua: UA | null = null;
  public activeCall: any = null;
  public activeChannel: CallState | null = null;
  public callsQueue: any[] = [];
  public holdCallsQueue: any[] = [];
  public player: any = null;
  public ringer: any = null;
  public ringbackTone: HTMLAudioElement | null = null;
  public connectedPhone: boolean = false;
  public config: SoftPhoneConfig | null = null;
  public initiated: boolean = false;
  public micMuted: boolean = false;

  public onUserAgentAction: () => void = () => {};
  public onCallActionConnection: (
    type: CallActionType,
    payload: any,
    data?: any,
  ) => void = () => {};
  public engineEvent: (event: EventType, payload?: any) => void = () => {};

  constructor() {
    this.notify = this.notify.bind(this);
    this.setMicMuted = this.setMicMuted.bind(this);
    this.hold = this.hold.bind(this);
    this.unhold = this.unhold.bind(this);
    this.call = this.call.bind(this);
    this.answer = this.answer.bind(this);
    this.hungup = this.hungup.bind(this);
    this.start = this.start.bind(this);
    this.stop = this.stop.bind(this);
  }

  notify = (message: string) => {
    this.onCallActionConnection("notify", message);
  };

  tmpEvent = () => {
    console.log("Active Call:", this.activeCall);
    console.log("Calls Queue:", this.callsQueue);
    console.log("Hold Calls Queue:", this.holdCallsQueue);
  };

  setMicMuted = () => {
    if (this.micMuted && this.activeCall) {
      this.activeCall.unmute();
      this.micMuted = false;
      this.onCallActionConnection("unmute", this.activeCall.id);
    } else if (!this.micMuted && this.activeCall) {
      this.micMuted = true;
      this.activeCall.mute();
      this.onCallActionConnection("mute", this.activeCall.id);
    }
  };

  hold = (sessionId: string) => {
    if (this.activeCall && this.activeCall.id === sessionId) {
      this.activeCall.hold();
    }
  };

  unhold = (sessionId: string) => {
    if (!this.activeCall) {
      const toUnhold = this.holdCallsQueue.find(
        (call) => call.id === sessionId,
      );
      if (toUnhold) {
        toUnhold.unhold();
      }
    } else {
      console.log("Please end all active calls to unhold");
      this.notify("Please end all active calls to unhold");
    }
  };

  playRing = () => {
    if (this.ringer?.current) {
      try {
        if (this.ringer?.current?.src) {
          console.log("Playing ringtone...");
          this.ringer.current.currentTime = 0;
          this.ringer.current
            .play()
            .catch((err: Error) => console.error("Ringtone play error:", err));
        } else {
          console.log("Pausing ringtone...");
          this.stopRing();
        }
      } catch (err) {
        console.error("Failed to play ringtone:", err);
      }
    }
  };

  stopRing = () => {
    if (this.ringer?.current) {
      this.ringer.current.currentTime = 0;
      this.ringer.current.pause();
    }
  };

  startRingback = () => {
    if (this.ringbackTone) {
      try {
        this.ringbackTone.currentTime = 0;
        this.ringbackTone
          .play()
          .catch((err: Error) =>
            console.error("Ringback tone play error:", err),
          );
      } catch (e) {
        console.error("Failed to play ringback tone:", e);
      }
    }
  };

  stopRingback = () => {
    if (this.ringbackTone) {
      try {
        this.ringbackTone.pause();
        this.ringbackTone.currentTime = 0;
      } catch (e) {
        console.error("Failed to stop ringback tone:", e);
      }
    }
  };

  removeCallFromQueue = (callId: string) => {
    this.callsQueue = this.callsQueue.filter((call) => call.id !== callId);
  };

  addCallToHoldQueue = (call: any) => {
    this.holdCallsQueue.push(call);
  };

  removeCallFromHoldQueue = (callId: string) => {
    this.holdCallsQueue = this.holdCallsQueue.filter(
      (call) => call.id !== callId,
    );
  };

  connectAudioStreams = (session: any) => {
    console.log("Connecting audio streams for session:", session.id);

    if (!session.connection) {
      console.error("No WebRTC connection available");
      return;
    }

    try {
      // Get remote streams using the modern approach
      const receivers = session.connection.getReceivers();
      console.log("Available receivers:", receivers.length);

      receivers.forEach((receiver: RTCRtpReceiver, index: number) => {
        if (receiver.track && receiver.track.kind === "audio") {
          console.log(`Audio receiver ${index}:`, {
            trackId: receiver.track.id,
            enabled: receiver.track.enabled,
            muted: receiver.track.muted,
            readyState: receiver.track.readyState,
          });

          // Create a MediaStream with the audio track
          const audioStream = new MediaStream([receiver.track]);
          console.log("Created audio stream:", audioStream.id);

          // Connect to audio player
          if (this.player?.current) {
            this.player.current.srcObject = audioStream;
            this.player.current.volume = 1.0;
            this.player.current.muted = false;

            // Force play
            this.player.current
              .play()
              .then(() => {
                console.log("Audio playback started successfully");
              })
              .catch((error: Error) => {
                console.error("Audio playback failed:", error);
              });

            console.log("Audio stream connected to player element");
          } else {
            console.warn("No audio player element available");
          }
        }
      });

      // Also try the legacy approach as fallback
      const remoteStreams = session.connection.getRemoteStreams();
      if (remoteStreams.length > 0) {
        const stream = remoteStreams[0];
        console.log("Using legacy remote stream:", stream.id);

        if (this.player?.current) {
          this.player.current.srcObject = stream;
          this.player.current.volume = 1.0;
          this.player.current.muted = false;

          this.player.current
            .play()
            .then(() => {
              console.log("Legacy audio playback started successfully");
            })
            .catch((error: Error) => {
              console.error("Legacy audio playback failed:", error);
            });
        }
      }
    } catch (error) {
      console.error("Error connecting audio streams:", error);
    }
  };

  call = (number: string) => {
    console.log("Attempting to call:", {
      number,
      isValidNumber: number.match(/^\+?[0-9]+$/),
      sipUri: `sip:${number}@${this.config?.domain}`,
      domain: this.config?.domain,
      hasUA: !!this.ua,
    });

    if (this.ua && number.match(/^\+?[0-9]+$/)) {
      try {
        const sipUri = `sip:${number}@${this.config?.domain}`;
        console.log("Making SIP call to:", sipUri);

        const session = this.ua.call(sipUri, {
          mediaConstraints: { audio: true, video: false },
        });

        if (session) {
          // Store the called number on the session for later reference
          (session as any).calledNumber = number;

          this.callsQueue.push(session);
          this.activeCall = session;

          console.log("Call session created:", {
            sessionId: session.id,
            calledNumber: number,
            direction: session.direction,
            timestamp: new Date().toISOString(),
          });

          // Track call timing
          const callStartTime = Date.now();

          session.on("connecting", () => {
            console.log("Call connecting to:", number);
            this.onCallActionConnection("outgoingCall", session, {
              ...session,
              remote_identity: { uri: { user: number } },
            });
          });

          session.on("progress", () => {
            this.startRingback();
            this.onCallActionConnection("callProgress", session.id);
          });

          session.on("accepted", () => {
            console.log("Outgoing call accepted:", session.id);
            this.stopRingback();

            // Listen for hold/unhold events
            session.on("hold", () => {
              console.log("Call placed on hold:", session.id);
              this.onCallActionConnection("hold", session.id);
            });

            session.on("unhold", () => {
              console.log("Call resumed from hold:", session.id);
              this.onCallActionConnection("unhold", session.id);
            });

            // Debug audio streams for outgoing calls
            if (session.connection) {
              console.log("Outgoing call WebRTC Connection established:", {
                sessionId: session.id,
                iceConnectionState: session.connection.iceConnectionState,
                connectionState: session.connection.connectionState,
                signalingState: session.connection.signalingState,
              });

              // Monitor connection state changes
              session.connection.oniceconnectionstatechange = () => {
                console.log(
                  "Outgoing call ICE Connection State changed:",
                  session.connection.iceConnectionState,
                );
              };

              session.connection.onconnectionstatechange = () => {
                console.log(
                  "Outgoing call Connection State changed:",
                  session.connection.connectionState,
                );
              };

              // Check audio streams
              session.connection
                .getRemoteStreams()
                .forEach((stream: MediaStream, index: number) => {
                  console.log(`Outgoing call remote stream ${index}:`, {
                    id: stream.id,
                    audioTracks: stream.getAudioTracks().length,
                    videoTracks: stream.getVideoTracks().length,
                    active: stream.active,
                  });

                  // Connect stream to audio element
                  if (this.player?.current) {
                    this.player.current.srcObject = stream;
                    console.log(
                      "Outgoing call remote stream connected to audio player",
                    );
                  }
                });
            }

            this.onCallActionConnection("callAccepted", session.id, {
              customPayload: 0,
            });
          });

          session.on("ended", (data: any) => {
            console.log("Outgoing call ended:", session.id, data);
            this.stopRingback();
            this.stopRing();

            // Clear active call if this was it
            if (this.activeCall?.id === session.id) {
              this.activeCall = null;
            }

            this.removeCallFromQueue(session.id);
            this.onCallActionConnection("callEnded", session.id);
          });

          session.on("failed", (event: any) => {
            const callDuration = Date.now() - callStartTime;
            console.log("Outgoing call failed:", {
              sessionId: session.id,
              calledNumber: number,
              reason: event?.cause || "Unknown reason",
              durationMs: callDuration,
              eventDetails: event,
            });
            this.stopRingback();
            this.stopRing();

            // Clear active call if this was it
            if (this.activeCall?.id === session.id) {
              this.activeCall = null;
            }

            this.removeCallFromQueue(session.id);
            this.onCallActionConnection("callRejected", session.id);
          });
        } else {
          console.error("Failed to create call session");
          this.notify("Failed to create call session");
        }
      } catch (error) {
        console.error("Error making call:", error);
        this.notify(
          `Failed to make call: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    } else {
      console.warn("Cannot make call:", {
        hasUA: !!this.ua,
        numberValid: number.match(/^\+?[0-9]+$/),
        number: number,
      });
      if (!this.ua) {
        this.notify("SIP not connected");
      } else if (!number.match(/^\+?[0-9]+$/)) {
        this.notify("Invalid phone number format");
      }
    }
  };

  answer = (sessionId: string) => {
    const session = this.callsQueue.find((call) => call.id === sessionId);
    if (session) {
      try {
        console.log("Answering call with session:", sessionId);

        // Provide explicit media options with RTCAnswerOptions
        const answerOptions = {
          mediaConstraints: {
            audio: true,
            video: false,
          },
          rtcAnswerOptions: {
            offerToReceiveAudio: true,
            offerToReceiveVideo: false,
          },
          rtcOfferConstraints: {
            offerToReceiveAudio: true,
            offerToReceiveVideo: false,
          },
        };

        console.log("Answer options:", answerOptions);
        session.answer(answerOptions);
        this.activeCall = session;
        this.stopRing();
      } catch (error) {
        console.error("Error answering call:", error);
        this.notify("Failed to answer call");
      }
    }
  };

  hungup = (sessionId: string) => {
    console.log('CallsFlowControl.hungup() called with sessionId:', sessionId);
    console.log('Active call:', this.activeCall?.id);
    console.log('Calls in queue:', this.callsQueue.map(c => c.id));

    if (this.activeCall && this.activeCall.id === sessionId) {
      console.log('Terminating active call:', sessionId);
      try {
        this.activeCall.terminate();
      } catch (error) {
        console.error('Error terminating active call:', error);
      }
      this.activeCall = null;
    } else {
      const session = this.callsQueue.find((call) => call.id === sessionId);
      if (session) {
        console.log('Terminating queued call (incoming ringing):', sessionId);
        try {
          // Use 486 "Busy Here" - some PBXs handle this better than 603
          session.terminate({
            status_code: 486,
            reason_phrase: 'Busy Here'
          });
        } catch (error) {
          console.error('Error terminating queued call:', error);
        }
        // The session's "ended" or "failed" event will fire and handle cleanup
      } else {
        console.warn('Session not found for hungup:', sessionId);
      }
    }

    // Stop ringtone and ringback immediately
    this.stopRing();
    this.stopRingback();
  };

  init = () => {
    if (!this.config) {
      console.error("No configuration provided");
      return;
    }

    console.log("CallsFlowControl.init() called with config:", {
      domain: this.config.domain,
      uri: this.config.uri,
      authorization_user: this.config.authorization_user,
      ws_servers: this.config.ws_servers,
      display_name: this.config.display_name,
      register: this.config.register,
      hasPassword: !!this.config.password,
      passwordLength: this.config.password?.length,
    });

    // Enable SIP debug logging
    debug.enable("JsSIP:*");
    console.log("SIP Debug enabled for all JsSIP modules");

    try {
      const socket = new WebSocketInterface(this.config.ws_servers);

      this.ua = new UA({
        sockets: [socket],
        uri: this.config.uri,
        password: this.config.password,
        display_name: this.config.display_name,
        register: this.config.register !== false,
        contact_uri: this.config.contact_uri,
        authorization_user: this.config.authorization_user,
        instance_id: this.config.instance_id,
        session_timers: this.config.session_timers,
        use_preloaded_route: this.config.use_preloaded_route,
      });

      this.ua.on("connecting", () => {
        this.engineEvent("connecting");
      });

      this.ua.on("connected", () => {
        this.engineEvent("connected");
      });

      this.ua.on("registered", () => {
        this.engineEvent("registered");
      });

      this.ua.on("disconnected", () => {
        this.engineEvent("disconnected");
      });

      this.ua.on("registrationFailed", () => {
        this.engineEvent("registrationFailed");
      });

      this.ua.on("newRTCSession", (data: any) => {
        const session = data.session;
        console.log("New RTC Session:", {
          direction: session.direction,
          sessionId: session.id,
          remoteIdentity: session.remote_identity,
          hasAudio: !!session.connection,
        });

        if (session.direction === "incoming") {
          this.callsQueue.push(session);
          if (this.activeCall) this.ringer = null;
          this.playRing();
          this.onCallActionConnection("incomingCall", session, session);

          session.on("accepted", () => {
            console.log("Incoming call accepted:", session.id);
            this.stopRing();
            this.activeCall = session;

            // Listen for hold/unhold events
            session.on("hold", () => {
              console.log("Call placed on hold:", session.id);
              this.onCallActionConnection("hold", session.id);
            });

            session.on("unhold", () => {
              console.log("Call resumed from hold:", session.id);
              this.onCallActionConnection("unhold", session.id);
            });

            // Debug audio streams
            if (session.connection) {
              console.log("WebRTC Connection established:", {
                sessionId: session.id,
                iceConnectionState: session.connection.iceConnectionState,
                connectionState: session.connection.connectionState,
                signalingState: session.connection.signalingState,
              });

              // Monitor connection state changes
              session.connection.oniceconnectionstatechange = () => {
                console.log(
                  "ICE Connection State changed:",
                  session.connection.iceConnectionState,
                );

                if (session.connection.iceConnectionState === "connected") {
                  console.log("ICE connection established successfully");
                  // Try to connect audio streams again when ICE is connected
                  this.connectAudioStreams(session);
                } else if (session.connection.iceConnectionState === "failed") {
                  console.error("ICE connection failed");
                }
              };

              session.connection.onconnectionstatechange = () => {
                console.log(
                  "Connection State changed:",
                  session.connection.connectionState,
                );

                if (session.connection.connectionState === "connected") {
                  console.log("WebRTC connection established successfully");
                  this.connectAudioStreams(session);
                }
              };

              // Check audio streams
              session.connection
                .getRemoteStreams()
                .forEach((stream: MediaStream, index: number) => {
                  console.log(`Remote stream ${index}:`, {
                    id: stream.id,
                    audioTracks: stream.getAudioTracks().length,
                    videoTracks: stream.getVideoTracks().length,
                    active: stream.active,
                  });

                  stream
                    .getAudioTracks()
                    .forEach((track: MediaStreamTrack, trackIndex: number) => {
                      console.log(`Remote audio track ${trackIndex}:`, {
                        id: track.id,
                        enabled: track.enabled,
                        muted: track.muted,
                        readyState: track.readyState,
                        kind: track.kind,
                      });
                    });

                  // Connect stream to audio element
                  if (this.player?.current) {
                    this.player.current.srcObject = stream;
                    console.log("Remote stream connected to audio player");
                  }
                });
            }

            this.onCallActionConnection("callAccepted", session.id, {
              customPayload: 0,
            });
          });

          session.on("ended", (data: any) => {
            console.log("Incoming call ended:", session.id, data);
            this.stopRing();
            this.stopRingback();

            // Clear active call if this was it
            if (this.activeCall?.id === session.id) {
              this.activeCall = null;
            }

            this.removeCallFromQueue(session.id);
            this.onCallActionConnection("callEnded", session.id);
          });

          session.on("failed", (data: any) => {
            console.log("Incoming call failed:", session.id, data);
            this.stopRing();
            this.stopRingback();

            // Clear active call if this was it
            if (this.activeCall?.id === session.id) {
              this.activeCall = null;
            }

            this.removeCallFromQueue(session.id);

            // Check if it was a rejection vs a failure
            const cause = data?.cause;
            if (cause === 'Rejected' || cause === 'Busy' || cause === 'Canceled') {
              this.onCallActionConnection("callRejected", session.id);
            } else {
              this.onCallActionConnection("callEnded", session.id);
            }
          });
        }
      });

      this.initiated = true;
    } catch (error) {
      console.error("Error initializing UA:", error);
      this.notify("Failed to initialize softphone");
    }
  };

  start = () => {
    if (this.ua) {
      try {
        this.ua.start();
      } catch (error) {
        console.error("Error starting UA:", error);
        this.notify("Failed to start softphone");
      }
    }
  };

  stop = () => {
    if (this.ua) {
      try {
        console.log('CallsFlowControl: Stopping UA and unregistering...');

        // Terminate all active sessions first
        if (this.ua.isConnected()) {
          // Unregister from the SIP server
          this.ua.unregister({
            all: true // Unregister all bindings
          });

          // Terminate the UA connection
          this.ua.stop();

          console.log('CallsFlowControl: UA stopped and unregistered');
        } else {
          console.log('CallsFlowControl: UA not connected, just stopping');
          this.ua.stop();
        }

        // Clear the UA reference
        this.ua = null;
      } catch (error) {
        console.error("Error stopping UA:", error);
      }
    } else {
      console.log('CallsFlowControl: No UA to stop');
    }
  };
}
